// src/services/firebase.js
// Single source of truth for Firebase init across The Art of Story.
// (AuthContext used to initialise its own copy; it now imports from here.)
//
// TWO Firebase apps when Auth Hub sign-in is enabled:
//   1. app / auth / db  → ddd-art-of-story (this app's own project). The
//      session the Firestore rules and the Knowledge Hub (Marlow) check.
//   2. authHubApp / authHubAuth → ddd-auth-hub-4ed14 (the shared hub). Carries
//      the Dive Deep Dialogues identity and cross-app entitlement claims.
// The hub's keyless spoke-token bridge mints a token for EACH; /auth/callback
// signs in to both. See src/services/authHub.js and the hub's
// docs/SPOKE_TOKEN_BRIDGE.md. Ported from The Art of Conversation.
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';

// Public web config from .env (see .env.example and FIREBASE_SETUP.md).
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.error('[firebase] VITE_FIREBASE_* is not set: sign-in and cloud backup will fail. See .env.example and FIREBASE_SETUP.md.');
}

// Auth Hub (shared sign-in) project config; see .env.example. The key is
// public web config, read from env so it isn't duplicated across the fleet.
const authHubConfig = {
  apiKey: import.meta.env.VITE_AUTH_HUB_API_KEY,
  authDomain: import.meta.env.VITE_AUTH_HUB_AUTH_DOMAIN || 'ddd-auth-hub-4ed14.firebaseapp.com',
  projectId: import.meta.env.VITE_AUTH_HUB_PROJECT_ID || 'ddd-auth-hub-4ed14',
};

// Auth Hub sign-in is on by default; a build can opt out (the old sign-in
// page, kept as a rollback) by setting the flag to 'false'. Without the hub
// key we skip the hub app: sign-in still works, because the spoke session
// comes from the token the hub redirects back with and needs no hub SDK; we
// just don't keep the hub-side session in this app.
const useAuthHub =
  import.meta.env.VITE_USE_AUTH_HUB !== 'false' && !!authHubConfig.apiKey;

if (import.meta.env.VITE_USE_AUTH_HUB !== 'false' && !authHubConfig.apiKey) {
  console.warn(
    '[firebase] VITE_AUTH_HUB_API_KEY is not set: hub session not kept in this app. See .env.example.'
  );
}

// Reuse the default app if it already exists, to prevent the duplicate-app error.
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// A separate NAMED app for the hub project, so it never collides with the
// default (spoke) app; both sessions then coexist in the same page.
export const authHubApp = useAuthHub
  ? (getApps().find((a) => a.name === 'auth-hub') || initializeApp(authHubConfig, 'auth-hub'))
  : null;

export const auth = getAuth(app);                                   // spoke session (rules, Knowledge Hub)
export const authHubAuth = authHubApp ? getAuth(authHubApp) : null; // hub session (identity)
export const db = getFirestore(app);
export const functions = getFunctions(app);

// No Firebase Analytics or Performance Monitoring: the analytics standard
// (docs/ANALYTICS_STANDARD.md) counts visits with Cloudflare Web Analytics,
// and Google Analytics may only load through @ddd/analytics after a yes.

export default app;
